import { useCallback, useMemo } from "react";
import { Badge, Box, Flex, Table, Text } from "@radix-ui/themes";
import {
  ThumbsUp,
  ThumbsDown,
  ListPlus,
  FileEdit,
  BookOpen,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { GearIcon } from "@radix-ui/react-icons";
import {
  getActivityContent,
  useActivityLog,
} from "../../context/useActivityLog";
import { useChat } from "../../context/ChatWrapper";
import { useSimulator } from "../../context/SimulatorWrapper";
import styles from "./styles.module.scss";
import { activityTagToBadge } from "./activityTable.types";
import { ReadOnlyMarkdown } from "@/v2/components/editor/ReadOnlyMarkdown";

interface ActivityTableProps {
  releaseId: number;
}

export function ActivityTable({ releaseId }: ActivityTableProps) {
  const navigate = useNavigate();
  const { simulatorId } = useParams();
  const { handleTopicSelect } = useChat();
  const { topics } = useSimulator();

  const { activities } = useActivityLog({
    filters: {
      selectedReleaseId: releaseId,
      selectedFilters: [],
      sortOrder: "newest",
      searchQuery: "",
    },
  });

  const displayedActivities = useMemo(
    () => activities.slice(0, 6),
    [activities]
  );
  const remainingCount = useMemo(
    () => Math.max(0, activities.length - 6),
    [activities]
  );

  const selectTopic = useCallback(
    (topic: string) => {
      if (!topics) return;

      const topicObj = topics.find((t) => t.topic === topic);
      if (topicObj) {
        handleTopicSelect(topicObj);
      }
    },
    [handleTopicSelect, topics]
  );

  if (activities.length === 0) {
    return (
      <Box className={styles.emptyFeedback}>
        <Text size="2" color="gray">
          No activity recorded.
        </Text>
      </Box>
    );
  }

  return (
    <Box className={styles.tableContainer}>
      <Table.Root>
        <Table.Body>
          {displayedActivities.map((activity) => (
            <Table.Row
              key={activity.id}
              className={styles.tableRow}
              onClick={() => {
                if (activity.topic && selectTopic) {
                  selectTopic(activity.topic);
                }
              }}
            >
              <Table.Cell>
                <Flex align="center" gap="3">
                  <Box className={styles.activityIcon}>
                    {activity.type === "Instruction" &&
                      activity.action === "none" ? (
                      <Badge size="2" color="green">
                        Live
                      </Badge>
                    ) : (
                      <>
                        {activity.type === "Instruction" && (
                          <ListPlus size={14} />
                        )}
                        {activity.type === "Brief" && <FileEdit size={14} />}
                        {activity.type === "Prerequisite" && (
                          <BookOpen size={14} />
                        )}
                        {activity.type === "Session" &&
                          activity.sessionTags?.includes("Good Feedback") && (
                            <Badge
                              size="2"
                              color="green"
                              className={styles.feedbackBadge}
                            >
                              <ThumbsUp size={14} />
                            </Badge>
                          )}
                        {activity.type === "Session" &&
                          activity.sessionTags?.includes("Bad Feedback") && (
                            <Badge
                              size="2"
                              color="red"
                              className={styles.feedbackBadge}
                            >
                              <ThumbsDown size={14} />
                            </Badge>
                          )}
                        {activity.type === "General" && (
                          <Badge
                            size="2"
                            color="blue"
                            className={styles.feedbackBadge}
                          >
                            <GearIcon />
                          </Badge>
                        )}
                      </>
                    )}
                  </Box>
                  <Flex
                    direction="column"
                    gap="1"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Flex gap="1">
                      <Text
                        size="2"
                        weight="medium"
                        style={{ color: "var(--gray-12)" }}
                      >
                        {activity.type === "Instruction" &&
                          activity.action === "none" ? (
                          "Live Topic"
                        ) : (
                          <>
                            {activity.type === "Instruction" &&
                              "Instruction added"}
                            {activity.type === "Brief" && "Brief updated"}
                            {activity.type === "Session" && "Session"}
                            {activity.type === "General" && "General"}
                            {activity.type === "Prerequisite" &&
                              "Prerequisite added"}
                          </>
                        )}
                      </Text>
                      {activity.type === "Session" &&
                        activity.sessionTags?.map(
                          (tag) => activityTagToBadge[tag]
                        )}
                    </Flex>
                    {(activity.type === "Instruction" ||
                      activity.type === "Brief" ||
                      activity.type === "Session" ||
                      activity.type === "General" ||
                      activity.type === "Prerequisite") && (
                        <div className={styles.markdownContentInline}>
                          <ReadOnlyMarkdown
                            content={getActivityContent(activity)
                              ?.replace(/---+/g, '')
                              ?.replace(/#{1,6}\s+(.+)$/gm, '**$1**')
                              ?.replace(/\\/g, '')
                              ?.replace(/\n/g, ' ')
                              ?.trim()
                            }
                          />
                        </div>
                      )}
                  </Flex>
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Text size="2" color="gray">
                  {activity.topic}
                </Text>
              </Table.Cell>
            </Table.Row>
          ))}
          {remainingCount > 0 && (
            <Table.Row
              className={`${styles.tableRow} ${styles.remainingCount}`}
              onClick={() => navigate(`/automation/${simulatorId}/activity`)}
              style={{ cursor: "pointer" }}
            >
              <Table.Cell colSpan={2}>
                <Text
                  size="2"
                  color="gray"
                  align="center"
                  style={{ padding: "8px 0" }}
                >
                  +{remainingCount} more activities
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}
